import React, { useState } from "react";
import { Link } from "react-router-dom";

import EpcScanner from "../components/EpcScanner";
import ProductPicker from "../components/ProductPicker";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { type ItemHit, lookupTag, matchTag } from "../services/tagApi";

/**
 * Match Tags — assign RFID tags to a catalog item (vendor + platform only).
 *
 * Two-step flow, mirroring gen5-pwa's TagItems: (1) pick the product + a
 * per-tag quantity, then (2) scan one or more tags (R6 / camera / manual) that
 * accumulate into a list — each is looked up so an already-matched tag is
 * flagged — and batch-match them all to the chosen product. Writes land in
 * mttr.rfid_tag (source='app'); re-matching an assigned EPC replaces it.
 */
type TagStatus = "checking" | "new" | "matched";

interface PendingTag {
  epc: string;
  status: TagStatus;
  existingSku?: string | null;
  existingName?: string | null;
}

const QTY_OK = (q: string): boolean => {
  if (q.trim() === "") return true;
  const n = Number(q);
  return Number.isInteger(n) && n >= 0;
};

export const MatchTagsPage: React.FC = () => {
  const { token } = useAuth();
  const [step, setStep] = useState<"product" | "tags">("product");
  const [selected, setSelected] = useState<ItemHit | null>(null);
  const [qty, setQty] = useState("1");
  const [tags, setTags] = useState<PendingTag[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const goToTags = (): void => {
    if (!selected) return;
    if (!QTY_OK(qty)) {
      setError("Quantity must be a whole number (0 or more), or blank.");
      return;
    }
    setError(null);
    setSavedMsg(null);
    setTags([]);
    setStep("tags");
  };

  const backToProduct = (): void => {
    setStep("product");
    setTags([]);
    setError(null);
  };

  // Add a scanned/typed EPC to the pending list, then look up whether it's
  // already matched so we can warn before replacing it.
  const addEpc = (raw: string): void => {
    if (!token) return;
    const epc = raw.trim().toUpperCase();
    if (!/^[A-Za-z0-9]{1,30}$/.test(epc)) return;
    setSavedMsg(null);
    setTags((prev) => (prev.some((t) => t.epc === epc) ? prev : [...prev, { epc, status: "checking" }]));
    lookupTag(epc, token)
      .then((res) => {
        setTags((prev) =>
          prev.map((t) =>
            t.epc === epc
              ? res.matched
                ? { epc, status: "matched", existingSku: res.sku, existingName: res.name }
                : { epc, status: "new" }
              : t,
          ),
        );
      })
      .catch(() => {
        setTags((prev) => prev.map((t) => (t.epc === epc ? { epc, status: "new" } : t)));
      });
  };

  const removeTag = (epc: string): void =>
    setTags((prev) => prev.filter((t) => t.epc !== epc));

  const doMatch = async (): Promise<void> => {
    if (!selected || !token || tags.length === 0) return;
    const q = qty.trim() === "" ? null : Number(qty);
    setSaving(true);
    setError(null);
    const results = await Promise.allSettled(
      tags.map((t) => matchTag(t.epc, selected.item_id, q, token)),
    );
    const failed: PendingTag[] = [];
    let ok = 0;
    results.forEach((r, i) => {
      if (r.status === "fulfilled") ok += 1;
      else failed.push(tags[i]);
    });
    setTags(failed);
    setSaving(false);
    const label = selected.sku ?? `#${selected.item_id}`;
    if (failed.length === 0) {
      setSavedMsg(`${ok} tag${ok === 1 ? "" : "s"} matched to ${label}. Scan more, or change product.`);
    } else {
      setError(`${ok} matched, ${failed.length} failed — left below to retry.`);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-2xl">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Match Tags</h1>
          <p className="text-xs text-secondary-500 mt-1">
            {step === "product"
              ? "Pick the product and quantity, then scan its tags."
              : "Scan the tags to assign to this product."}
          </p>
        </div>
        <Link to="/tags/manage" className="text-xs text-primary-600 hover:underline">
          Manage tags →
        </Link>
      </header>

      {savedMsg && (
        <div className="mb-3 text-sm text-success-700 bg-success-50 px-3 py-2 rounded">
          ✓ {savedMsg}
        </div>
      )}
      {error && (
        <div className="mb-3 text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {/* Step indicator */}
      <ol className="flex items-center gap-2 text-xs text-secondary-500 mb-4">
        <li className={step === "product" ? "font-semibold text-primary-700" : ""}>
          1. Product
        </li>
        <li aria-hidden>›</li>
        <li className={step === "tags" ? "font-semibold text-primary-700" : ""}>2. Tags</li>
      </ol>

      {step === "product" ? (
        <div className="bg-white border border-secondary-200 rounded-md p-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-secondary-500 mb-1">Product</div>
            {selected ? (
              <div className="flex items-center justify-between gap-2 border border-primary-200 bg-primary-50 rounded px-3 py-2">
                <div className="min-w-0">
                  <div className="font-mono text-sm">{selected.sku ?? `#${selected.item_id}`}</div>
                  {selected.name && (
                    <div className="text-xs text-secondary-500 truncate">{selected.name}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="text-xs text-secondary-500 hover:underline shrink-0"
                >
                  change
                </button>
              </div>
            ) : (
              <ProductPicker token={token ?? ""} onSelect={setSelected} autoFocus />
            )}
          </div>

          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
                Quantity / tag
              </label>
              <input
                type="number"
                min={0}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-24 border border-secondary-300 rounded px-2 py-1.5 text-sm text-right"
              />
            </div>
            <Button
              type="button"
              disabled={!selected || !QTY_OK(qty)}
              onClick={goToTags}
              className="ml-auto"
            >
              Next: scan tags →
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Selected product banner */}
          <div className="bg-white border border-secondary-200 rounded-md p-3 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-mono text-sm">{selected?.sku ?? `#${selected?.item_id}`}</div>
              {selected?.name && (
                <div className="text-xs text-secondary-500 truncate">{selected.name}</div>
              )}
              <div className="text-xs text-secondary-400 mt-0.5">
                {qty.trim() === "" ? "no qty" : `${qty} per tag`}
              </div>
            </div>
            <button
              type="button"
              onClick={backToProduct}
              className="text-xs text-primary-600 hover:underline shrink-0"
            >
              ← change product
            </button>
          </div>

          <EpcScanner onEpc={addEpc} />

          {/* Pending tags */}
          <div className="bg-white border border-secondary-200 rounded-md">
            <div className="px-3 py-2 border-b border-secondary-100 text-xs uppercase tracking-wide text-secondary-500">
              Tags to match ({tags.length})
            </div>
            {tags.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-secondary-500">
                Scan or enter a tag above — each one lands here.
              </div>
            ) : (
              <ul className="divide-y divide-secondary-100">
                {tags.map((t) => (
                  <li key={t.epc} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0">
                      <span className="font-mono text-sm">{t.epc}</span>
                      {t.status === "checking" && (
                        <span className="ml-2 text-xs text-secondary-400">checking…</span>
                      )}
                      {t.status === "matched" && (
                        <span className="ml-2 text-xs text-warning-700">
                          already on {t.existingSku ?? "another item"} — will replace
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeTag(t.epc)}
                      className="text-secondary-400 hover:text-secondary-700 text-lg leading-none shrink-0"
                      aria-label={`Remove ${t.epc}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end">
            <Button
              type="button"
              disabled={tags.length === 0 || saving}
              onClick={() => void doMatch()}
            >
              {saving
                ? "Matching…"
                : `Match ${tags.length} tag${tags.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchTagsPage;
